import type { OrderChargeCost, OrderForInvoicing } from '@my/tms-api';

/**
 * Who pays a charge: its own bill-to, else the order's invoice-to, else the
 * order's customer. The server applies the same rule (OrderPayer) and refuses
 * an invoice that puts a charge in front of anyone else.
 */
export function chargePayer(
  order: Pick<OrderForInvoicing, 'customerPartyId' | 'invoiceToPartyId'>,
  charge: Pick<OrderChargeCost, 'billToPartyId'>,
): string {
  return charge.billToPartyId ?? order.invoiceToPartyId ?? order.customerPartyId;
}

/**
 * One order's charges for one payer. An order whose charges go to two parties
 * is two of these; `customerPartyId` is the PAYER and `id` stays the real order
 * id (only one unit per order can sit in any one invoice, so the builder keys
 * by it safely). `unitKey` is unique across the queue.
 */
export type PayerUnit = OrderForInvoicing & {
  unitKey: string;
  /** The order's own customer, when someone else is paying for this part. */
  onBehalfOfPartyId: string | null;
};

export function splitByPayer(order: OrderForInvoicing): PayerUnit[] {
  const fallback = order.invoiceToPartyId ?? order.customerPartyId;
  const byPayer = new Map<string, OrderForInvoicing['charges']>();
  for (const ch of order.charges) {
    const payer = chargePayer(order, ch);
    const arr = byPayer.get(payer);
    if (arr) arr.push(ch);
    else byPayer.set(payer, [ch]);
  }
  if (byPayer.size === 0) byPayer.set(fallback, []);
  return [...byPayer].map(([payer, charges]) => ({
    ...order,
    customerPartyId: payer,
    charges,
    unitKey: `${order.id}:${payer}`,
    onBehalfOfPartyId: payer === order.customerPartyId ? null : order.customerPartyId,
  }));
}
